import * as Api from '@ViewModels';
import moment from 'moment';
import { getDefaultDateStringValue, getDefaultTimeStringValue } from '../../../../models/UiUtils';
import { SendEmailFrom } from '../../../../viewmodels/AppViewModels';
import { CampaignSessionManager } from './campaignSessionManager';
import { IAIScheduleSuccessProps, ResourceType } from './types';

/**
 * Extract AI context from props based on resource type
 */
export const getAIContextFromProps = (props: IAIScheduleSuccessProps): Api.AIContext | null => {
	const { resourceType, emailComposer, socialPost, blogPost } = props;

	switch (resourceType) {
		case 'email':
			return emailComposer ? CampaignSessionManager.createAIContextFromEmailData(emailComposer) : null;
		case 'social':
			return socialPost ? CampaignSessionManager.createAIContextFromSocialData(socialPost) : null;
		case 'blog':
			return blogPost
				? CampaignSessionManager.createAIContextFromBlogData(
						blogPost.title || 'Blog Article',
						blogPost.content || { document: '', source: '' }
					)
				: CampaignSessionManager.createAIContextFromBlogData('Blog Article', { document: '', source: '' });
		default:
			return null;
	}
};

/**
 * Determine source type from AI context
 */
export const getSourceTypeFromContent = (content: Api.AIContext | null): string | null => {
	if (!content) return null;

	if ('_type' in content) {
		switch (content._type) {
			case 'EmailAIContext':
				return 'email';
			case 'SocialMediaAIContext':
				return 'social';
			case 'BlogAIContext':
				return 'blog';
			default:
				return null;
		}
	}

	return null;
};

/**
 * Extract scheduled date from props
 */
export const getScheduledDateFromProps = (props: IAIScheduleSuccessProps): Date | undefined => {
	const { resourceType, estimate, socialPost, blogPost } = props;

	switch (resourceType) {
		case 'email':
			return estimate?.estimate?.startDate ? new Date(estimate.estimate.startDate) : undefined;
		case 'social':
			return socialPost?.dueMoment ? socialPost.dueMoment.toDate() : undefined;
		case 'blog':
			return blogPost?.scheduledSendDate ? moment(blogPost.scheduledSendDate).toDate() : undefined;
		default:
			return undefined;
	}
};

/**
 * Get unified scheduled message for any resource type
 * This eliminates duplication across components
 */
export const getScheduledMessage = (
	resourceType: ResourceType,
	date: Date,
	_props?: IAIScheduleSuccessProps
): string => {
	switch (resourceType) {
		case 'email':
			return `Your emails will start to send on ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}.`;

		case 'social': {
			const isSendNow = moment(date).diff(moment(), 'minutes') <= 10;
			return isSendNow
				? 'Your post will be delivered in the next 5 minutes.'
				: `Your post will be delivered on ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}.`;
		}

		case 'blog':
			return `Your blog will be published on ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}.`;

		default:
			return `Scheduled for ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}`;
	}
};

/**
 * Format scheduled date for display (legacy - use getScheduledMessage instead)
 * @deprecated Use getScheduledMessage for consistency
 */
export const formatScheduledDate = (date: Date, resourceType?: ResourceType): string => {
	if (resourceType === 'social') {
		const isSendNow = moment(date).diff(moment(), 'minutes') <= 10;
		return isSendNow
			? 'Your post will be delivered in the next 5 minutes.'
			: `Your post will be delivered on ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}.`;
	}

	return `Scheduled for ${getDefaultDateStringValue(date)} at ${getDefaultTimeStringValue(date)}`;
};

/**
 * Get recipient info from email composer
 */
export const getRecipientInfo = (emailComposer: any): string | null => {
	if (!emailComposer?.emailMessage) return null;

	let who = '';
	const isSendFromOption = emailComposer?.emailMessage?.isSendFromOption;

	if (isSendFromOption) {
		if (isSendFromOption(SendEmailFrom.ContactOwner) && (emailComposer as any).ownersWithFirstNames?.length > 0) {
			const ownersWithFirstNames = (emailComposer as any).ownersWithFirstNames;
			who = ownersWithFirstNames
				.map(
					(owner: any, i: number) =>
						`${
							i === ownersWithFirstNames.length - 1 && ownersWithFirstNames.length > 1 ? 'and ' : ''
						}${owner.user.firstName} (${owner.contactsCount})`
				)
				.join(ownersWithFirstNames.length > 2 ? ', ' : ' ');
		} else if (isSendFromOption(SendEmailFrom.SelectedUser)) {
			who = `${emailComposer.emailMessage.options.sendEmailFromUser.firstName}`;
		}
	}

	return who ? `${who} will be notified via email.` : null;
};

/**
 * Calculate estimated days to complete
 */
export const getEstimatedDaysToComplete = (props: IAIScheduleSuccessProps, emailComposer?: any): number => {
	const { estimate } = props;
	const composer = emailComposer || props.emailComposer;

	if (!composer) return 0;

	const composerForCalc = composer as any;
	const approxForCalc = composerForCalc?.resourceSelector
		? composerForCalc.approximation
		: composer?.emailMessage?.contactEmailApproximation;

	let value = -1;
	if (composerForCalc?.emailMessage?.options?.minimumDurationInDays > 0) {
		value = composerForCalc.emailMessage.options.minimumDurationInDays;
	} else if (composerForCalc?.emailMessage?.sendLimits?.length > 0) {
		value = Math.ceil((approxForCalc?.hasEmail || 1) / composerForCalc.emailMessage.sendLimits[0].count);
	}

	if (value < 0) {
		value =
			!!estimate && !!estimate.estimate
				? Math.ceil(moment(estimate.estimate.endDate).diff(moment(estimate.estimate.startDate), 'days', true))
				: -1;
	}

	return value < 0 ? 0 : value;
};

/**
 * Get daily send limit
 */
export const getDailySendLimit = (props: IAIScheduleSuccessProps, emailComposer?: any): any => {
	const { estimate } = props;
	const composer = emailComposer || props.emailComposer;

	if (!composer) return null;

	if ((composer as any)?.emailMessage?.options?.minimumDurationInDays > 0) {
		return null;
	}
	return estimate ? estimate?.emailWorkload?.limits?.find(x => x.intervalInMinutes === 1440) : null;
};

/**
 * Get number of emails to send
 */
export const getNumberOfEmailsToSend = (emailComposer?: any): number => {
	if (!emailComposer) return 1;

	const composerWithResource = emailComposer as any;
	const approximation = composerWithResource?.resourceSelector
		? composerWithResource.approximation
		: emailComposer?.emailMessage?.contactEmailApproximation;

	let numberOfEmailsToSend = approximation?.hasEmail || emailComposer?.estimatedRecipientsTotal || 1;
	if ((emailComposer as any)?.followUpSource) {
		numberOfEmailsToSend = (emailComposer as any)?.followUpCount;
	}

	return numberOfEmailsToSend;
};

/**
 * Check if social post should send now
 */
export const isSocialSendNow = (socialPost?: Api.SocialMediaPostViewModel): boolean => {
	return socialPost?.dueMoment ? moment(socialPost.dueMoment).diff(moment(), 'minutes') <= 10 : false;
};
